/**
 * The console's side of the memory explorer: a project's memory items (`MemoryItem`, packages/shared
 * schemas/projectMemory.ts) as what the workbench's Memory control draws (memoryGlobeModel.ts): graph nodes with their
 * topic, the graph's links, and the written items as ring items. No DOM, no React.
 *
 *   node        a concept: a node of the memory layer, its topic the first group (community) that holds it, or its
 *               repository when no group does; its folders come from the file it was read from
 *   edge        a link between two concepts (drawn faint on the map, followed on selection), or between two written items
 *   community   no node of its own: it names the topic of its members
 *   note, artifact      the Library ring
 *   lesson, decision    the Governance ring
 *
 * An edge names its ends by id or, in older pushes, by name: both are resolved, as the server's `explain` does.
 */
import { memoryGraphDigest, type MemoryItem, type MemoryItemKind } from '@aioe/shared';

import type { ArmsItem, ArmsItemLink, ArmsTierId } from './armsModel.ts';
import { buildAdjacency, buildGlobe, type Globe, type GlobeGraphNode, globeSources, type LinkKind, type Neighbour } from './memoryGlobeModel.ts';

/** What the picture reads of an item: no label, no summary or text. The worker is sent only this. */
export type ExplorerItem = Pick<MemoryItem, 'id' | 'kind' | 'rootId' | 'updatedAt' | 'data'> & { deletedAt?: string };

/** The data fields the picture reads. */
const DRAWN_FIELDS = ['name', 'title', 'sourceFile', 'source', 'members', 'from', 'to', 'relation', 'type'] as const;

/** An item cut down to what the picture reads, for the worker. */
export function explorerItem(i: MemoryItem): ExplorerItem {
  const data: Record<string, unknown> = {};
  for (const k of DRAWN_FIELDS) if (i.data[k] !== undefined) data[k] = i.data[k];
  return { id: i.id, kind: i.kind, rootId: i.rootId, updatedAt: i.updatedAt, data, ...(i.deletedAt ? { deletedAt: i.deletedAt } : {}) };
}

export interface ExplorerInput {
  graph: GlobeGraphNode[];
  links: Array<{ source: string; target: string; relation: string }>;
  items: ArmsItem[];
  itemLinks: ArmsItemLink[];
  /** A ring item's id to when it last changed (ms), for the Timeline. */
  changed: Map<string, number>;
  /** What the picture would draw, as one string: the same digest means the same picture. */
  digest: string;
}

/** Which ring a written item goes on, and the group it is told apart by there. */
const RING: Partial<Record<MemoryItemKind, { tier: ArmsTierId; group: string; words: string }>> = {
  note: { tier: 'library', group: 'Notes', words: 'note notes' },
  artifact: { tier: 'library', group: 'Artifacts', words: 'artifact artifacts' },
  lesson: { tier: 'decisions', group: 'Lessons', words: 'lesson lessons' },
  decision: { tier: 'decisions', group: 'Decisions', words: 'decision decisions' },
};

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** What a person calls an item: its title, its name, or its id. */
export function memoryItemTitle(i: Pick<ExplorerItem, 'id' | 'data'>): string {
  return str(i.data.title) ?? str(i.data.name) ?? i.id;
}

/** The memory item id behind a key, or undefined for a hub, a folder or a layer. */
export function itemIdOfKey(key: string): string | undefined {
  if (key.startsWith('node:')) return key.slice(5);
  if (key.startsWith('item:')) return key.slice(5);
  return undefined;
}

/**
 * The explorer's input from the items this reader holds. `rootNames` names the repositories (a concept in no group sits
 * under its repository's name). Deterministic: the same items in any order give the same input.
 */
export function explorerInput(source: Iterable<ExplorerItem>, rootNames: ReadonlyMap<string, string> = new Map()): ExplorerInput {
  const all = [...source].filter((i) => !i.deletedAt).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const byId = new Map<string, ExplorerItem>();
  const byName = new Map<string, ExplorerItem>();
  for (const i of all) {
    byId.set(i.id, i);
    if (i.kind === 'edge' || i.kind === 'community') continue;
    const name = memoryItemTitle(i).toLowerCase();
    if (!byName.has(name)) byName.set(name, i);
  }
  const resolve = (ref: unknown): ExplorerItem | undefined => {
    if (typeof ref !== 'string' || !ref) return undefined;
    const byRef = byId.get(ref);
    const hit = byRef && byRef.kind !== 'edge' && byRef.kind !== 'community' ? byRef : byName.get(ref.toLowerCase());
    return hit;
  };

  // A concept's topic: the first group (by id) that lists it.
  const topicOf = new Map<string, string>();
  for (const c of all) {
    if (c.kind !== 'community' || !Array.isArray(c.data.members)) continue;
    const name = memoryItemTitle(c);
    for (const m of c.data.members as unknown[]) {
      const n = resolve(m);
      if (n && n.kind === 'node' && !topicOf.has(n.id)) topicOf.set(n.id, name);
    }
  }
  const repoWord = (rootId: string) => (rootId ? (rootNames.get(rootId) ?? rootId) : 'Project-level');

  const graph: GlobeGraphNode[] = [];
  const items: ArmsItem[] = [];
  const changed = new Map<string, number>();
  for (const i of all) {
    if (i.kind === 'node') {
      const file = str(i.data.sourceFile);
      graph.push({ id: i.id, label: memoryItemTitle(i), community_name: topicOf.get(i.id) ?? repoWord(i.rootId), ...(file ? { source_file: file } : {}), changed_at: i.updatedAt });
      continue;
    }
    const ring = RING[i.kind];
    if (!ring) continue;
    const t = Date.parse(i.updatedAt);
    if (Number.isFinite(t)) changed.set(i.id, t);
    const where = str(i.data.source) ?? (i.rootId ? repoWord(i.rootId) : undefined);
    items.push({ id: i.id, label: memoryItemTitle(i), tier: ring.tier, group: ring.group, words: ring.words, ...(where ? { detail: where } : {}) });
  }

  const links: ExplorerInput['links'] = [];
  const itemLinks: ArmsItemLink[] = [];
  for (const e of all) {
    if (e.kind !== 'edge') continue;
    const a = resolve(e.data.from);
    const b = resolve(e.data.to);
    if (!a || !b || a === b) continue;
    const relation = str(e.data.relation) ?? str(e.data.type) ?? 'linked';
    if (a.kind === 'node' && b.kind === 'node') links.push({ source: a.id, target: b.id, relation });
    else if (a.kind !== 'node' && b.kind !== 'node') itemLinks.push({ source: a.id, target: b.id, relation });
    // A link from a concept to a written item has no line to draw between a shell and a ring; the item's card names it.
  }

  const digest = memoryGraphDigest({
    graph: {
      nodes: [...graph, ...items.map((i) => ({ id: `item:${i.id}`, label: i.label, community_name: i.group ?? null, ...(i.detail ? { source_file: i.detail } : {}), ...(changed.has(i.id) ? { changed_at: String(changed.get(i.id)) } : {}) }))],
      links: [...links, ...itemLinks.map((l) => ({ source: `item:${l.source}`, target: `item:${l.target}`, relation: l.relation }))],
    },
  });
  return { graph, links, items, itemLinks, changed, digest };
}

/** The kind of an item in a word, for the card. */
export const KIND_WORD: Record<MemoryItemKind, string> = { node: 'Concept', edge: 'Relation', community: 'Group', note: 'Note', lesson: 'Lesson', decision: 'Decision', artifact: 'Artifact', knowledge: 'Knowledge' };

/** The picture, built off the main thread (memoryExplorer.worker.ts): the nodes, who is linked to whom, and the links by node index. */
export interface ExplorerPicture {
  globe: Globe;
  /** Concepts left off to keep the picture drawable. */
  truncated: number;
  adjacency: number[][];
  /** The graph's links and the links between written items, as node-index pairs, with each one's words. */
  wires: Array<readonly [number, number]>;
  wireWords: string[];
  xlinks: Array<readonly [number, number]>;
  xlinkWords: string[];
  digest: string;
}

/** Build the picture from the input: what the workbench's ArmsView does with its nodes, for the console's. */
export function explorerPicture(input: ExplorerInput): ExplorerPicture {
  const { sources, truncated } = globeSources(input.items, input.graph, { graphLinks: input.links, changed: input.changed });
  const globe = buildGlobe(sources, { structure: true });
  const pairs = (links: ReadonlyArray<{ source: string; target: string; relation: string }>, prefix: string) => {
    const at: Array<readonly [number, number]> = [];
    const words: string[] = [];
    for (const l of links) {
      const a = globe.byKey.get(`${prefix}${l.source}`);
      const b = globe.byKey.get(`${prefix}${l.target}`);
      if (a && b) {
        at.push([a.index, b.index]);
        words.push(l.relation);
      }
    }
    return { at, words };
  };
  const wires = pairs(input.links, 'node:');
  const xlinks = pairs(input.itemLinks, 'item:');
  return { globe, truncated, adjacency: buildAdjacency(globe, input.links, input.itemLinks), wires: wires.at, wireWords: wires.words, xlinks: xlinks.at, xlinkWords: xlinks.words, digest: input.digest };
}

/**
 * One node's neighbours, as the workbench's `buildNeighbours` lists them for every node (the same kinds, words, counts and
 * order), worked out for the selection only: for every node it took 75 ms at 28,000 items.
 */
export function neighboursOf(p: Pick<ExplorerPicture, 'globe' | 'wires' | 'wireWords' | 'xlinks' | 'xlinkWords'>, index: number): Neighbour[] {
  const { globe } = p;
  const by = new Map<number, Neighbour>();
  const put = (to: number, kind: LinkKind, relation: string) => {
    if (to === index) return;
    const at = by.get(to);
    if (at) {
      at.count += 1;
      if (!at.relation && relation) at.relation = relation;
    } else by.set(to, { index: to, kind, relation, count: 1 });
  };
  p.wires.forEach(([a, b], i) => (a === index ? put(b, 'wire', p.wireWords[i] ?? 'linked') : b === index ? put(a, 'wire', p.wireWords[i] ?? 'linked') : undefined));
  p.xlinks.forEach(([a, b], i) => (a === index ? put(b, 'xlink', p.xlinkWords[i] ?? '') : b === index ? put(a, 'xlink', p.xlinkWords[i] ?? '') : undefined));
  for (const e of globe.edges) {
    if (e.a !== index && e.b !== index) continue;
    const from = e.a === index;
    const other = from ? e.b : e.a;
    if (e.kind === 'route') put(other, 'route', from ? 'holds' : 'in layer');
    else put(other, 'spoke', from ? 'holds' : globe.nodes[e.a]!.struct === 'dir' ? 'in folder' : 'in group');
  }
  return [...by.values()].sort((x, y) => y.count - x.count || globe.nodes[y.index]!.weight - globe.nodes[x.index]!.weight || x.index - y.index);
}
